import { WithdrawalJournal, withdrawalJournal } from "./withdrawal-journal";
import { runOutsideAngularZone } from "./outside-zone";
import {
  JOURNAL_DELIVERY_SOURCE,
  type JournalRecord,
  type JournalState,
} from "./withdrawal-journal.types";
import type {
  WithdrawalHeartbeatCommand,
  WithdrawalHeartbeatReply,
  WithdrawalHeartbeatStep,
} from "./withdrawal-heartbeat-state";

export class WithdrawalHeartbeat {
  private createWorker?: () => Worker;
  private worker?: Worker;
  private timer?: ReturnType<typeof setInterval>;
  private step: WithdrawalHeartbeatStep | null = null;
  private state?: JournalState;
  private uid?: string | null;
  private unavailable = false;
  private readonly candidates = new Map<
    string,
    {
      record: JournalRecord;
      rejectionRecord: JournalRecord;
      step: WithdrawalHeartbeatStep;
      frozen: boolean;
      settled: boolean;
      resumed: boolean;
    }
  >();

  constructor(
    private readonly journal: WithdrawalJournal = withdrawalJournal
  ) {}

  install(createWorker: () => Worker): void {
    if (this.createWorker) return;
    this.createWorker = createWorker;
    this.journal.subscribeState((state) => {
      if (this.uid !== state.uid) {
        this.stop();
        this.step = null;
      }
      this.uid = state.uid;
      this.state = state;
      this.update();
    });
  }

  setStep(step: WithdrawalHeartbeatStep | null): void {
    this.step = step;
    this.update();
  }

  private update(): void {
    if (this.unavailable || !this.state?.enabled || !this.createWorker) {
      this.stop();
      return;
    }
    if (!this.step) {
      if (this.timer !== undefined) clearInterval(this.timer);
      this.timer = undefined;
      try {
        const command: WithdrawalHeartbeatCommand = { type: "idle" };
        this.worker?.postMessage(command);
      } catch {
        this.fail("send_failed");
      }
      return;
    }
    if (!this.worker) {
      const start = () => {
        try {
          const worker = this.createWorker!();
          this.worker = worker;
          worker.addEventListener(
            "message",
            (event: MessageEvent<WithdrawalHeartbeatReply>) => {
              if (this.worker !== worker || !this.state?.enabled) return;
              try {
                this.receive(event.data);
              } catch {
                this.fail("message_failed");
              }
            }
          );
          worker.addEventListener("error", () => {
            if (this.worker === worker) this.fail("worker_error");
          });
          worker.addEventListener("messageerror", () => {
            if (this.worker === worker) this.fail("message_failed");
          });
        } catch {
          this.fail("creation_failed");
        }
      };
      runOutsideAngularZone(start);
    }
    if (this.worker && this.timer === undefined) {
      const startTimer = () =>
        (this.timer = setInterval(() => this.sendBeat(), 500));
      runOutsideAngularZone(startTimer);
    }
    this.sendBeat();
  }

  private receive(reply: WithdrawalHeartbeatReply): void {
    const id = reply.type === "frozen" ? reply.record.id : reply.recordId;
    const candidate = this.candidates.get(id);
    const currentUid = this.state?.uid;
    if (!currentUid || candidate?.record.uid !== currentUid) return;
    const original = candidate.record;
    if (reply.type === "frozen") {
      if (
        candidate.frozen ||
        !WithdrawalHeartbeat.matchesRecord(
          reply.record,
          original,
          "withdrawal.page_frozen"
        )
      )
        return;
      candidate.frozen = true;
      this.journal.append(reply.record);
      return;
    }
    if (!candidate.frozen || reply.uid !== original.uid) return;
    if (reply.type === "resumed") {
      if (candidate.resumed) return;
      candidate.resumed = true;
      this.journal.record("withdrawal.page_resumed", {
        tags: {
          last_step: candidate.step,
          frozen_record_id: original.id,
          visibility: document.visibilityState,
        },
        extra: { stuck_ms: reply.stuckMs },
      });
      return;
    }
    if (candidate.settled) return;
    if (reply.type === "delivery_failed") {
      this.journal.recordDeliveryFailure(
        JOURNAL_DELIVERY_SOURCE.Worker,
        reply.uid,
        { ...reply, recordCount: 1 }
      );
    } else if (reply.type === "acknowledged") {
      candidate.settled = true;
      this.journal.acknowledge([original.id]);
    } else if (reply.type === "rejected") {
      const notice = candidate.rejectionRecord;
      if (
        reply.record.uid !== original.uid ||
        !WithdrawalHeartbeat.matchesRecord(
          reply.record,
          notice,
          "withdrawal.journal_rejected"
        )
      )
        return;
      candidate.settled = true;
      this.journal.acknowledge([original.id]);
      this.journal.append(reply.record);
    }
  }

  private static matchesRecord(
    record: JournalRecord,
    reserved: JournalRecord,
    event: "withdrawal.page_frozen" | "withdrawal.journal_rejected"
  ): boolean {
    return (
      record.id === reserved.id &&
      record.uid === reserved.uid &&
      record.pageLoadId === reserved.pageLoadId &&
      record.seq === reserved.seq &&
      record.event === event
    );
  }

  private sendBeat(): void {
    if (!this.worker || !this.step || !this.state?.enabled) return;
    try {
      const record = this.journal.reserveRecord("withdrawal.page_frozen", {
        level: "warning",
        tags: {
          flow: "withdrawal",
          last_step: this.step,
          visibility: document.visibilityState,
        },
      });
      const rejectionRecord = this.journal.reserveRecord(
        "withdrawal.journal_rejected",
        {
          level: "warning",
          tags: { flow: "withdrawal", source: JOURNAL_DELIVERY_SOURCE.Worker },
          extra: { record_count: 1 },
        }
      );
      if (!record || !rejectionRecord) return;
      const beat: WithdrawalHeartbeatCommand = {
        type: "beat",
        step: this.step,
        token: this.state.token,
        visibility: document.visibilityState,
        record,
        rejectionRecord,
      };
      this.candidates.set(record.id, {
        record,
        rejectionRecord,
        step: this.step,
        frozen: false,
        settled: false,
        resumed: false,
      });
      if (this.candidates.size > 300)
        this.candidates.delete(this.candidates.keys().next().value!);
      this.worker.postMessage(beat);
    } catch {
      this.fail("send_failed");
    }
  }

  private fail(
    reason:
      | "creation_failed"
      | "worker_error"
      | "send_failed"
      | "message_failed"
  ): void {
    if (this.unavailable) return;
    this.unavailable = true;
    this.stop();
    try {
      this.journal.record("withdrawal.heartbeat_unavailable", {
        level: "warning",
        tags: { reason, last_step: this.step ?? "unknown" },
      });
    } catch {
      this.journal.reportFailure("heartbeat");
    }
  }

  private stop(): void {
    if (this.timer !== undefined) clearInterval(this.timer);
    this.timer = undefined;
    const worker = this.worker;
    this.worker = undefined;
    this.candidates.clear();
    try {
      worker?.terminate();
    } catch {
      this.journal.reportFailure("heartbeat");
    }
  }
}

export const withdrawalHeartbeat = new WithdrawalHeartbeat();
